import { useState, useEffect, useCallback, useRef } from "react";
import { flush as flushRetryQueue } from "../utils/retryQueue";

/**
 * Monitors browser connectivity via navigator.onLine + the online/offline events.
 * Triggers retry queue flush on reconnect.
 *
 * Returns { isOnline, wasOffline }
 *   isOnline  , current connectivity state
 *   wasOffline, true for 3 s after coming back online (used for "Back online!" message)
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const wasOfflineTimer = useRef(null);

  const goOnline = useCallback(() => {
    setIsOnline(true);
    setWasOffline(true);
    clearTimeout(wasOfflineTimer.current);
    wasOfflineTimer.current = setTimeout(() => setWasOffline(false), 4000);
    // Flush any queued offline requests
    flushRetryQueue();
  }, []);

  const goOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(false);
    clearTimeout(wasOfflineTimer.current);
  }, []);

  useEffect(() => {
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
      clearTimeout(wasOfflineTimer.current);
    };
  }, [goOnline, goOffline]);

  return { isOnline, wasOffline };
}
